
export interface Theme {
  id: string;
  name: string;
  isCustom: boolean;
  colors: {
    // Header colors
    headerBackground: string;
    headerForeground: string;

    // Side menu colors
    sideMenuBackground: string;
    sideMenuForeground: string;
    sideMenuHover: string;
    sideMenuActive: string;
    /*
     * The rail's painted surface, as a two-stop vertical gradient.
     *
     * These were the one part of the sidebar a theme could not reach. The
     * container paints `--theme-sidemenu-grad-from/to` over sideMenuBackground,
     * and only index.css set them — so every preset drew its foreground, hover
     * and active colours over the DEFAULT theme's surface. That was survivable
     * while the default surface was white and near enough to the presets' own.
     * It stopped being survivable when the default became a deep violet: an
     * unmigrated preset would paint dark text onto it.
     *
     * Optional so a custom theme saved before this existed still loads; the
     * setter falls back to sideMenuBackground, which is what such a theme meant
     * by "the rail" in the first place.
     */
    sideMenuGradFrom?: string;
    sideMenuGradTo?: string;

    // Body colors
    bodyBackground: string;
    bodyForeground: string;

    // Brand colors
    primary: string;
    secondary: string;
    accent?: string;

    // Text colors
    textPrimary: string;
    textSecondary: string;
    textMuted: string;

    // Background variants
    backgroundSecondary: string;
    backgroundTertiary: string;

    // Border colors
    border: string;
    borderLight: string;

    // Logo/Brand colors
    logoBackground: string;
    logoForeground: string;
    brandAccent: string;
  };
}

// Helper function to determine if a color is light or dark
const isLightColor = (color: string): boolean => {
  /*
   * Non-hex values reach here now that hover is `rgba(255,255,255,0.08)` — a
   * wash, not a fill. The old code ran parseInt over "rgba(255," and produced
   * NaN, and `NaN > 128` is false, so it happened to answer "dark" and pick
   * light text. Right answer, no reasoning behind it.
   *
   * A translucent hover is a veil over the rail, so what decides the text colour
   * is the rail underneath, not the veil. There is no way to know that from this
   * one string — so say "dark" deliberately: every rail this app ships is dark
   * except Brown-Theme, whose hover is an opaque hex and never lands here.
   */
  if (!color || !color.trim().startsWith('#')) return false;

  // Convert hex to RGB
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate perceived brightness using standard formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

const PREDEFINED_THEMES: Theme[] = [
  {
    id: 'default-white',
    name: 'Default Aurora',
    isCustom: false,
    colors: {
      headerBackground: '#ffffff',
      headerForeground: '#111827',
      // Ink rail. Matches the :root block in index.css exactly — the two must
      // agree, because whichever loads last decides what is on screen.
      sideMenuBackground: '#0b0b11',
      sideMenuGradFrom: '#12121a',
      sideMenuGradTo: '#0a0a0f',
      // Deliberately NOT #ffffff. The DOM sweep below paints this onto every
      // label and icon in the rail, so it is what "an idle row" looks like —
      // and idle rows are muted so the current one can be white without a glow.
      sideMenuForeground: '#b0b2c2',
      sideMenuHover: 'rgba(255, 255, 255, 0.055)',
      sideMenuActive: 'rgba(139, 92, 246, 0.17)',
      bodyBackground: '#f6f4fb',
      bodyForeground: '#111827',
      primary: '#7c3aed',
      secondary: '#6d28d9',
      accent: '#8b5cf6',
      textPrimary: '#111827',
      textSecondary: '#6b7280',
      textMuted: '#9ca3af',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#f5f3ff',
      border: '#e5e7eb',
      borderLight: '#f3f4f6',
      logoBackground: '#7c3aed',
      logoForeground: '#ffffff',
      brandAccent: '#8b5cf6'
    }
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    isCustom: false,
    colors: {
      headerBackground: '#1e40af',
      headerForeground: '#ffffff',
      sideMenuBackground: '#1f2937',
      sideMenuGradFrom: '#2b3a4f',
      sideMenuGradTo: '#1f2937',
      sideMenuForeground: '#e5e7eb',
      sideMenuHover: '#374151',
      sideMenuActive: '#3b82f6',
      bodyBackground: '#f9fafb',
      bodyForeground: '#111827',
      primary: '#3b82f6',
      secondary: '#6366f1',
      accent: '#06b6d4',
      textPrimary: '#111827',
      textSecondary: '#6b7280',
      textMuted: '#9ca3af',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#f3f4f6',
      border: '#e5e7eb',
      borderLight: '#f3f4f6',
      logoBackground: '#ffffff',
      logoForeground: '#1e40af',
      brandAccent: '#06b6d4'
    }
  },
  {
    id: 'emerald-green',
    name: 'Emerald Forest',
    isCustom: false,
    colors: {
      headerBackground: '#059669',
      headerForeground: '#ffffff',
      sideMenuBackground: '#064e3b',
      sideMenuGradFrom: '#0a6b51',
      sideMenuGradTo: '#064e3b',
      sideMenuForeground: '#d1fae5',
      sideMenuHover: '#065f46',
      sideMenuActive: '#10b981',
      bodyBackground: '#f0fdf4',
      bodyForeground: '#14532d',
      primary: '#10b981',
      secondary: '#06b6d4',
      accent: '#8b5cf6',
      textPrimary: '#14532d',
      textSecondary: '#047857',
      textMuted: '#6b7280',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#ecfdf5',
      border: '#d1fae5',
      borderLight: '#ecfdf5',
      logoBackground: '#ffffff',
      logoForeground: '#059669',
      brandAccent: '#10b981'
    }
  },
  {
    id: 'purple-magic',
    name: 'Purple Magic',
    isCustom: false,
    colors: {
      headerBackground: '#7c3aed',
      headerForeground: '#ffffff',
      sideMenuBackground: '#581c87',
      sideMenuGradFrom: '#6d28a8',
      sideMenuGradTo: '#4a1573',
      sideMenuForeground: '#e9d5ff',
      sideMenuHover: '#6b21a8',
      sideMenuActive: '#a855f7',
      bodyBackground: '#faf5ff',
      bodyForeground: '#581c87',
      primary: '#a855f7',
      secondary: '#ec4899',
      accent: '#f59e0b',
      textPrimary: '#581c87',
      textSecondary: '#7c3aed',
      textMuted: '#6b7280',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#f3e8ff',
      border: '#e9d5ff',
      borderLight: '#f3e8ff',
      logoBackground: '#ffffff',
      logoForeground: '#7c3aed',
      brandAccent: '#a855f7'
    }
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    isCustom: false,
    colors: {
      headerBackground: '#ea580c',
      headerForeground: '#ffffff',
      sideMenuBackground: '#9a3412',
      sideMenuGradFrom: '#b8461c',
      sideMenuGradTo: '#7c2a0f',
      sideMenuForeground: '#fed7aa',
      sideMenuHover: '#c2410c',
      sideMenuActive: '#f97316',
      bodyBackground: '#fffbeb',
      bodyForeground: '#9a3412',
      primary: '#f97316',
      secondary: '#eab308',
      accent: '#ef4444',
      textPrimary: '#9a3412',
      textSecondary: '#c2410c',
      textMuted: '#6b7280',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#fef3c7',
      border: '#fed7aa',
      borderLight: '#fef3c7',
      logoBackground: '#ffffff',
      logoForeground: '#ea580c',
      brandAccent: '#f97316'
    }
  },
  {
    id: 'dark-slate',
    name: 'Dark Slate',
    isCustom: false,
    colors: {
      headerBackground: '#0f172a',
      headerForeground: '#f1f5f9',
      sideMenuBackground: '#1e293b',
      sideMenuGradFrom: '#2b3a4f',
      sideMenuGradTo: '#1e293b',
      sideMenuForeground: '#cbd5e1',
      sideMenuHover: '#334155',
      sideMenuActive: '#3b82f6',
      bodyBackground: '#f8fafc',
      bodyForeground: '#0f172a',
      primary: '#3b82f6',
      secondary: '#64748b',
      accent: '#06b6d4',
      textPrimary: '#0f172a',
      textSecondary: '#475569',
      textMuted: '#64748b',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#f1f5f9',
      border: '#e2e8f0',
      borderLight: '#f1f5f9',
      logoBackground: '#3b82f6',
      logoForeground: '#ffffff',
      brandAccent: '#06b6d4'
    }
  },
  {
    id: 'brown-theme',
    name: 'Brown-Theme',
    isCustom: false,
    colors: {
      headerBackground: '#ffffff',
      headerForeground: '#222222',
      sideMenuBackground: '#f8f8f8',
      sideMenuGradFrom: '#ffffff',
      sideMenuGradTo: '#f8f8f8',
      sideMenuForeground: '#374151',
      sideMenuHover: '#fdf8f0',
      sideMenuActive: '#e9a33c',
      bodyBackground: '#ffffff',
      bodyForeground: '#222222',
      primary: '#e9a33c',
      secondary: '#6b7280',
      accent: '#f59e0b',
      textPrimary: '#222222',
      textSecondary: '#6b7280',
      textMuted: '#9ca3af',
      backgroundSecondary: '#fdf8f0',
      backgroundTertiary: '#f9fafb',
      border: '#e5e7eb',
      borderLight: '#f3f4f6',
      logoBackground: '#e9a33c',
      logoForeground: '#ffffff',
      brandAccent: '#f59e0b'
    }
  }
];

export class ThemeService {
  private static readonly STORAGE_KEY = 'app_themes';
  private static readonly CURRENT_THEME_KEY = 'current_theme';
  private static originalTheme: Theme | null = null;

  static getAllThemes(): Theme[] {
    const customThemes = this.getCustomThemes();
    return [...PREDEFINED_THEMES, ...customThemes];
  }

  static getCustomThemes(): Theme[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static getCurrentTheme(): Theme {
    const currentId = localStorage.getItem(this.CURRENT_THEME_KEY);
    if (currentId) {
      const theme = this.getAllThemes().find(t => t.id === currentId);
      if (theme) return theme;
    }
    return PREDEFINED_THEMES[0]; // Default to first predefined theme (Default White)
  }

  static getSuperClaudeTheme(): Theme {
    const brownTheme = PREDEFINED_THEMES.find(t => t.id === 'brown-theme');
    if (brownTheme) {
      return brownTheme;
    }
    // Fallback to default if Brown-Theme not found
    return PREDEFINED_THEMES[0];
  }

  static createSuperClaudeTheme(): Theme {
    return {
      id: `brown-theme-${Date.now()}`,
      name: 'Brown-Theme Custom',
      isCustom: true,
      colors: {
        headerBackground: '#ffffff',
        headerForeground: '#222222',
        sideMenuBackground: '#f8f8f8',
        sideMenuGradFrom: '#ffffff',
        sideMenuGradTo: '#f8f8f8',
        sideMenuForeground: '#374151',
        sideMenuHover: '#fdf8f0',
        sideMenuActive: '#e9a33c',
        bodyBackground: '#ffffff',
        bodyForeground: '#222222',
        primary: '#e9a33c',
        secondary: '#6b7280',
        accent: '#f59e0b',
        textPrimary: '#222222',
        textSecondary: '#6b7280',
        textMuted: '#9ca3af',
        backgroundSecondary: '#fdf8f0',
        backgroundTertiary: '#f9fafb',
        border: '#e5e7eb',
        borderLight: '#f3f4f6',
        logoBackground: '#e9a33c',
        logoForeground: '#ffffff',
        brandAccent: '#f59e0b'
      }
    };
  }

  static saveTheme(theme: Theme): void {
    const customThemes = this.getCustomThemes();
    customThemes.push(theme);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(customThemes));
  }

  static updateTheme(theme: Theme): void {
    if (theme.isCustom) {
      let customThemes = this.getCustomThemes();
      const index = customThemes.findIndex(t => t.id === theme.id);
      if (index !== -1) {
        customThemes[index] = theme;
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(customThemes));
      }
    }
  }

  static deleteTheme(themeId: string): void {
    const customThemes = this.getCustomThemes();
    const filtered = customThemes.filter(t => t.id !== themeId);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
  }

  static applyTheme(theme: Theme): void {
    this.setThemeColors(theme);
    localStorage.setItem(this.CURRENT_THEME_KEY, theme.id);
    this.originalTheme = null;
  }

  static previewTheme(theme: Theme): void {
    if (!this.originalTheme) {
      this.originalTheme = this.getCurrentTheme();
    }
    this.setThemeColors(theme);
  }

  static cancelPreview(): void {
    if (this.originalTheme) {
      this.setThemeColors(this.originalTheme);
      this.originalTheme = null;
    }
  }

  static createDefaultTheme(): Theme {
    return {
      id: Date.now().toString(),
      name: 'Custom Theme',
      isCustom: true,
      colors: { ...PREDEFINED_THEMES[0].colors }
    };
  }

  private static setThemeColors(theme: Theme): void {
    const root = document.documentElement;
    const { colors } = theme;

    // Set CSS custom properties
    /*
     * The header's own two tokens are NOT written from a preset.
     *
     * The bar paints --theme-header-grad-*, which no preset carries, so it stays
     * dark whatever headerBackground says; writing headerForeground alongside it
     * put dark text (#111827 in every light preset) on that dark bar. index.css
     * owns --theme-header-bg / --theme-header-fg / --theme-header-fg-muted.
     */
    root.style.setProperty('--theme-sidemenu-bg', colors.sideMenuBackground);
    // The rail's painted surface. Falls back to the flat background for a custom
    // theme saved before these existed — a flat rail, but the theme's own rail.
    root.style.setProperty('--theme-sidemenu-grad-from', colors.sideMenuGradFrom ?? colors.sideMenuBackground);
    root.style.setProperty('--theme-sidemenu-grad-to', colors.sideMenuGradTo ?? colors.sideMenuBackground);
    root.style.setProperty('--theme-sidemenu-fg', colors.sideMenuForeground);
    root.style.setProperty('--theme-sidemenu-hover', colors.sideMenuHover);
    // Derive hover foreground color - use dark text for light hover bg, light text for dark hover bg
    const hoverFg = isLightColor(colors.sideMenuHover) ? '#1f2937' : '#f3f4f6';
    root.style.setProperty('--theme-sidemenu-hover-fg', hoverFg);
    root.style.setProperty('--theme-sidemenu-active', colors.sideMenuActive);
    root.style.setProperty('--theme-body-bg', colors.bodyBackground);
    root.style.setProperty('--theme-body-fg', colors.bodyForeground);
    root.style.setProperty('--theme-primary', colors.primary);
    root.style.setProperty('--theme-secondary', colors.secondary);
    root.style.setProperty('--theme-accent', colors.accent || colors.primary);
    root.style.setProperty('--theme-text-primary', colors.textPrimary);
    root.style.setProperty('--theme-text-secondary', colors.textSecondary);
    root.style.setProperty('--theme-text-muted', colors.textMuted);
    root.style.setProperty('--theme-bg-secondary', colors.backgroundSecondary);
    root.style.setProperty('--theme-bg-tertiary', colors.backgroundTertiary);
    root.style.setProperty('--theme-border', colors.border);
    root.style.setProperty('--theme-border-light', colors.borderLight);
    root.style.setProperty('--theme-logo-bg', colors.logoBackground);
    root.style.setProperty('--theme-logo-fg', colors.logoForeground);
    root.style.setProperty('--theme-brand-accent', colors.brandAccent);

    // Apply theme class to body for additional styling
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${theme.id}`);

    // Apply theme colors directly to components
    this.applyThemeToComponents(colors);
  }

  private static applyThemeToComponents(colors: Theme['colors']): void {
    /*
     * THE HEADER IS NOT STAMPED IMPERATIVELY ANY MORE.
     *
     * This used to walk every span/div/button/i inside `header` and write
     * `style.color = colors.headerForeground` on it. Two things went wrong:
     *
     *  1. The bar is dark in both themes, so a light preset's #111827 landed as
     *     near-black on near-black — the account name vanished entirely, while
     *     the role chip beside it (rendered a tick later, after the user loaded,
     *     so the walk never reached it) kept the correct light colour. That
     *     mismatch is what the bar looked broken from.
     *  2. An inline style beats every rule in index.css, including the
     *     `[data-theme="header"]` block written specifically to colour this bar,
     *     and React never clears it — nothing could win it back.
     *
     * The header now takes its colours from CSS alone. Same for the hover wash
     * that was bound here: `[data-theme="header"] button:hover` covers it, and
     * these listeners were re-added on every theme apply and never removed.
     */

    // Apply to sidebar with proper text colors
    const sidebars = document.querySelectorAll('[data-theme="sidebar"]');
    sidebars.forEach(el => {
      (el as HTMLElement).style.backgroundColor = colors.sideMenuBackground;
      (el as HTMLElement).style.color = colors.sideMenuForeground;
      (el as HTMLElement).style.borderColor = colors.border;
      
      /*
       * The brand block is skipped. It is the one thing on the rail that is NOT
       * light-on-violet: a near-white badge carrying deep-violet "AP". This walk
       * used to repaint that span sideMenuForeground (#ffffff) a tick after React
       * set its inline #5b21b6 — white letters on a white badge, so the badge read
       * as empty. Inline beats the stylesheet, so nothing could win it back.
       */
      const inBrand = (node: Element) => !!node.closest('.sidebar-logo-section');

      // Apply text color to all text elements in sidebar
      const textElements = el.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, div, a');
      textElements.forEach(textEl => {
        if (!textEl.closest('.active') && !textEl.closest('[class*="bg-blue"]') && !inBrand(textEl)) {
          (textEl as HTMLElement).style.color = colors.sideMenuForeground;
        }
      });

      // Apply to icons in sidebar
      const icons = el.querySelectorAll('i');
      icons.forEach(icon => {
        if (!icon.closest('.active') && !icon.closest('[class*="bg-blue"]') && !inBrand(icon)) {
          (icon as HTMLElement).style.color = colors.sideMenuForeground;
        }
      });

      // Apply hover styles
      const menuItems = el.querySelectorAll('button, a, [class*="hover"]');
      menuItems.forEach(item => {
        item.addEventListener('mouseenter', () => {
          if (!item.classList.contains('active') && !item.closest('.active')) {
            (item as HTMLElement).style.backgroundColor = colors.sideMenuHover;
          }
        });
        item.addEventListener('mouseleave', () => {
          if (!item.classList.contains('active') && !item.closest('.active')) {
            (item as HTMLElement).style.backgroundColor = 'transparent';
          }
        });
      });

      // Apply active styles
      const activeItems = el.querySelectorAll('.active, [class*="bg-blue"]');
      activeItems.forEach(item => {
        (item as HTMLElement).style.backgroundColor = colors.sideMenuActive;
        // The active pill is sideMenuActive (brand violet), so its label follows
        // the RAIL's foreground. It read headerForeground before — a different
        // panel's token, and #111827 in every light preset, i.e. near-black text
        // on a violet pill.
        (item as HTMLElement).style.color = colors.sideMenuForeground;

        // Apply to child elements
        const childElements = item.querySelectorAll('span, i');
        childElements.forEach(child => {
          (child as HTMLElement).style.color = colors.sideMenuForeground;
        });
      });
    });

    // Apply to logo/brand section
    const logoSections = document.querySelectorAll('[data-theme="logo"], .logo-section');
    logoSections.forEach(el => {
      (el as HTMLElement).style.backgroundColor = colors.logoBackground;
      (el as HTMLElement).style.color = colors.logoForeground;
      (el as HTMLElement).style.borderColor = colors.border;
      
      const textElements = el.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, div');
      textElements.forEach(textEl => {
        (textEl as HTMLElement).style.color = colors.logoForeground;
      });
    });

    // Apply to main content
    const mainContents = document.querySelectorAll('main, [data-theme="main"]');
    mainContents.forEach(el => {
      (el as HTMLElement).style.backgroundColor = colors.bodyBackground;
      (el as HTMLElement).style.color = colors.bodyForeground;
    });

    // Apply to cards and panels
    const cards = document.querySelectorAll('.bg-white, .dark\\:bg-gray-800');
    cards.forEach(el => {
      if (!el.classList.contains('fixed') && !el.classList.contains('absolute')) {
        (el as HTMLElement).style.backgroundColor = colors.backgroundSecondary;
        (el as HTMLElement).style.borderColor = colors.border;
      }
    });

    // Apply primary color to buttons and links
    const primaryElements = document.querySelectorAll('.bg-blue-600, .text-blue-600, .border-blue-600');
    primaryElements.forEach(el => {
      if (el.classList.contains('bg-blue-600')) {
        (el as HTMLElement).style.backgroundColor = colors.primary;
      }
      if (el.classList.contains('text-blue-600')) {
        (el as HTMLElement).style.color = colors.primary;
      }
      if (el.classList.contains('border-blue-600')) {
        (el as HTMLElement).style.borderColor = colors.primary;
      }
    });

    // Apply to borders
    const borderElements = document.querySelectorAll('[class*="border-gray"]');
    borderElements.forEach(el => {
      (el as HTMLElement).style.borderColor = colors.border;
    });
  }

  /**
   * Bump when a release changes what a theme is expected to CARRY, not merely
   * what the built-in ones look like.
   *
   * A predefined theme is read from the code above, so editing one reaches every
   * user on the next load. A CUSTOM theme is a frozen copy in localStorage — it
   * cannot gain a field that way. When the aurora rail added sideMenuGradFrom /
   * sideMenuGradTo, every stored custom theme was suddenly a theme missing two
   * fields, and the setter's fallback painted the rail in its flat
   * sideMenuBackground: a white rail, on an app that no longer has one.
   */
  private static readonly SCHEMA_VERSION = 2;
  private static readonly SCHEMA_VERSION_KEY = 'app_theme_schema_version';

  /**
   * Bring stored custom themes up to the current schema, once.
   *
   * Deliberately conservative: it fills in the two new fields from the theme's
   * own background and leaves every other colour alone, so somebody who built a
   * custom theme keeps it. It does NOT force the aurora onto them — a custom
   * theme is a choice, and silently overwriting one is worse than an old-looking
   * rail. Only a theme that never made a choice about the rail gets the default.
   */
  private static migrateStoredThemes(): void {
    const stored = parseInt(localStorage.getItem(this.SCHEMA_VERSION_KEY) ?? '1', 10);
    if (stored >= this.SCHEMA_VERSION) return;

    try {
      const customThemes = this.getCustomThemes();
      let touched = 0;
      for (const theme of customThemes) {
        if (!theme?.colors || theme.colors.sideMenuGradFrom) continue;
        theme.colors.sideMenuGradFrom = theme.colors.sideMenuBackground;
        theme.colors.sideMenuGradTo = theme.colors.sideMenuBackground;
        touched++;
      }
      if (touched) localStorage.setItem(this.STORAGE_KEY, JSON.stringify(customThemes));
      console.info(`[theme] migrated ${touched} custom theme(s) to schema v${this.SCHEMA_VERSION}`);
    } catch (err) {
      // A corrupt themes blob must not stop the app booting — the worst case is
      // that the migration runs again next load.
      console.warn('[theme] could not migrate stored themes:', err);
      return;
    }

    localStorage.setItem(this.SCHEMA_VERSION_KEY, String(this.SCHEMA_VERSION));
  }

  static initializeTheme(): void {
    this.migrateStoredThemes();
    const currentTheme = this.getCurrentTheme();
    this.applyTheme(currentTheme);

    // If dark mode is active, apply dark mode overrides
    if (this.isDarkModeActive()) {
      this.applyDarkModeOverrides();
    }
  }

  /**
   * Check if dark mode is currently active
   */
  static isDarkModeActive(): boolean {
    return document.documentElement.classList.contains('dark');
  }

  /**
   * Get dark mode colors for sidebar and header
   */
  static getDarkModeColors(): Partial<Theme['colors']> {
    return {
      // Dark mode takes the rail down rather than changing what it is, so the
      // two themes read as one identity at two levels of light. Matches the
      // `.dark` block in index.css.
      headerBackground: '#14101f',
      headerForeground: '#f3f4f6',
      sideMenuBackground: '#08080c',
      sideMenuGradFrom: '#0d0d13',
      sideMenuGradTo: '#060609',
      sideMenuForeground: '#a6a8b8',
      sideMenuHover: 'rgba(255, 255, 255, 0.05)',
      sideMenuActive: 'rgba(139, 92, 246, 0.19)',
      bodyBackground: '#100d18',
      bodyForeground: '#f3f4f6',
      backgroundSecondary: '#1f2937',
      backgroundTertiary: '#374151',
      border: '#374151',
      borderLight: '#4b5563',
    };
  }

  /**
   * Apply dark mode overrides to sidebar and header
   * Call this when dark mode is toggled
   */
  static applyDarkModeOverrides(): void {
    const isDark = this.isDarkModeActive();
    const root = document.documentElement;

    if (isDark) {
      const darkColors = this.getDarkModeColors();
      // Header tokens are owned by index.css in both themes — see setThemeColors.
      root.style.setProperty('--theme-sidemenu-bg', darkColors.sideMenuBackground!);
      root.style.setProperty('--theme-sidemenu-grad-from', darkColors.sideMenuGradFrom!);
      root.style.setProperty('--theme-sidemenu-grad-to', darkColors.sideMenuGradTo!);
      root.style.setProperty('--theme-sidemenu-fg', darkColors.sideMenuForeground!);
      root.style.setProperty('--theme-sidemenu-hover', darkColors.sideMenuHover!);
      const hoverFg = isLightColor(darkColors.sideMenuHover!) ? '#1f2937' : '#f3f4f6';
      root.style.setProperty('--theme-sidemenu-hover-fg', hoverFg);
      root.style.setProperty('--theme-body-bg', darkColors.bodyBackground!);
      root.style.setProperty('--theme-body-fg', darkColors.bodyForeground!);
      root.style.setProperty('--theme-bg-secondary', darkColors.backgroundSecondary!);
      root.style.setProperty('--theme-bg-tertiary', darkColors.backgroundTertiary!);
      root.style.setProperty('--theme-border', darkColors.border!);
      root.style.setProperty('--theme-border-light', darkColors.borderLight!);

      // Apply dark mode to components with inline styles
      this.applyDarkModeToComponents();
    } else {
      // Re-apply the current theme for light mode
      const currentTheme = this.getCurrentTheme();
      this.setThemeColors(currentTheme);
    }
  }

  /**
   * Apply dark mode styles to components that use inline styles
   */
  private static applyDarkModeToComponents(): void {
    const darkColors = this.getDarkModeColors();

    // The header is left to CSS here too — see applyThemeToComponents. Stamping
    // it inline in one branch and not the other is how the two themes drifted.

    // Apply to sidebar
    const sidebars = document.querySelectorAll('[data-theme="sidebar"]');
    sidebars.forEach(el => {
      (el as HTMLElement).style.backgroundColor = darkColors.sideMenuBackground!;
      (el as HTMLElement).style.color = darkColors.sideMenuForeground!;
      (el as HTMLElement).style.borderColor = darkColors.border!;

      // Brand block skipped for the same reason as the light path above — the
      // white "AP" badge is the one non-light-on-violet thing on the rail.
      const inBrand = (node: Element) => !!node.closest('.sidebar-logo-section');

      const textElements = el.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, div, a');
      textElements.forEach(textEl => {
        if (!textEl.closest('.active') && !textEl.closest('[class*="bg-blue"]') && !inBrand(textEl)) {
          (textEl as HTMLElement).style.color = darkColors.sideMenuForeground!;
        }
      });

      const icons = el.querySelectorAll('i');
      icons.forEach(icon => {
        if (!icon.closest('.active') && !icon.closest('[class*="bg-blue"]') && !inBrand(icon)) {
          (icon as HTMLElement).style.color = darkColors.sideMenuForeground!;
        }
      });
    });

    // Apply to main content
    const mainContents = document.querySelectorAll('main, [data-theme="main"]');
    mainContents.forEach(el => {
      (el as HTMLElement).style.backgroundColor = darkColors.bodyBackground!;
      (el as HTMLElement).style.color = darkColors.bodyForeground!;
    });
  }
}
